import { ILocation, IPhoto, TLocationEnums } from '@ftd/types'

import { BASE_URL } from '@/api/constants'
import { secureFetch } from '@/api/secure-fetch'

export const apiCreateLocation = async (data: Partial<ILocation>): Promise<{ data: ILocation }> => {
  const res = await secureFetch(`${BASE_URL}/locations`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to create location')
  return res.json()
}

export const apiGetLocations = async (params?: { filter?: any }): Promise<{ docs: ILocation[]; total: number }> => {
  const searchParams = new URLSearchParams()

  if (params?.filter) {
    Object.keys(params.filter).forEach((key) => {
      searchParams.append(`filter[${key}]`, params.filter[key])
    })
  } else {
    searchParams.append('filter[paginate][skip]', '0')
    searchParams.append('filter[paginate][limit]', '5')
  }

  const url = `${BASE_URL}/locations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

  const res = await secureFetch(url, {
    next: { revalidate: 60 },
  })

  if (!res.ok) throw new Error('Failed to fetch locations')
  return res.json()
}

export const apiGetLocationsServer = async (
  token: string,
  params?: {
    filter?: any
  },
): Promise<{ docs: ILocation[]; total: number }> => {
  const searchParams = new URLSearchParams()

  if (params?.filter) {
    Object.keys(params.filter).forEach((key) => {
      searchParams.append(`filter[${key}]`, params.filter[key])
    })
  } else {
    searchParams.append('filter[paginate][skip]', '0')
    searchParams.append('filter[paginate][limit]', '5')
  }

  const url = `${BASE_URL}/locations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

  const res = await fetch(url, {
    cache: 'no-store',
    headers: {
      token,
      'Content-Type': 'application/json',
    },
  })

  if (!res.ok) throw new Error('Failed to fetch locations')
  return res.json()
}

export const apiGetLocation = async (id?: string): Promise<ILocation<string>> => {
  const res = await secureFetch(`${BASE_URL}/locations/${id}`, {
    cache: 'no-store',
  })
  if (!res.ok) throw new Error('Failed to fetch location')
  return res.json()
}

export const apiUpdateLocation = async (id: string, data: Partial<ILocation<string>>): Promise<ILocation> => {
  const res = await secureFetch(`${BASE_URL}/locations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to update location')
  return res.json()
}

export const apiUploadPhotos = async (id: string, photos: File[]): Promise<IPhoto[]> => {
  const formData = new FormData()
  photos.forEach((photo) => formData.append('files', photo))
  const res = await secureFetch(`${BASE_URL}/locations/${id}/photos`, {
    method: 'PUT',
    body: formData,
  })
  if (!res.ok) throw new Error('Failed to upload photos')
  return res.json()
}

export const apiDeleteLocationPhoto = async (id: string, photoId: string): Promise<void> => {
  const res = await secureFetch(`${BASE_URL}/locations/${id}/photos/${photoId}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to delete photo')
  return res.json()
}

export const apiRenameLocationPhoto = async (id: string, photoId: string, name: string): Promise<void> => {
  const res = await secureFetch(`${BASE_URL}/locations/${id}/photos/${photoId}/name`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw new Error('Failed to rename photo')
  return res.json()
}

export const apiGetLocationEnums = async (): Promise<TLocationEnums> => {
  const res = await secureFetch(`${BASE_URL}/locations/enums`)
  if (!res.ok) throw new Error('Failed to fetch location enums')
  return res.json()
}
